import { useContext, useEffect } from 'react'
import { SITE_URL, SITE_NAME, CONTACT, SOCIAL, absoluteUrl } from '../../config/site'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'
import { HREFLANG, LOCALIZED_LANGS, OG_LOCALE, isLocalized, localizedPath, splitLocale } from '../../config/i18nRoutes'

const DEFAULT_TITLE = 'MLMind — Machine Learning & AI Solutions'
const DEFAULT_DESCRIPTION =
  'MLMind builds machine learning and AI products for healthcare, architecture, and e-commerce — from medical image analysis to BIM automation and conversational assistants.'

const truncate = (text, max = 160) => {
  if (!text) return ''
  const clean = String(text).replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean
  return clean.slice(0, max - 1).replace(/\s\S*$/, '') + '…'
}

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

// <link rel="alternate" hreflang> for every language version of the page,
// plus x-default. Removed again on pages that have no language versions.
const syncAlternates = (base) => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove())
  if (!isLocalized(base)) return
  const add = (hreflang, path) => {
    const el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', hreflang)
    el.setAttribute('href', absoluteUrl(path))
    document.head.appendChild(el)
  }
  add(HREFLANG.en, base)
  LOCALIZED_LANGS.forEach((lang) => add(HREFLANG[lang], localizedPath(base, lang)))
  add('x-default', base)
}

const upsertJsonLd = (data) => {
  let el = document.getElementById('seo-jsonld')
  if (!data) {
    if (el) el.remove()
    return
  }
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = 'seo-jsonld'
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

// 1200×630 card: the logo and tagline on white. Every page without its own
// cover image shares it — previously that fallback was the CRA React atom.
const FALLBACK_IMAGE = `${SITE_URL}/og-image.png`

// Bundled images resolve to a hashed path like /static/media/x.123.jpg —
// social scrapers need that as an absolute URL on the canonical origin.
//
// SVG is deliberately rejected: several article covers are SVGs, and neither
// Facebook nor X will render one as a preview, so those fall back to the PNG
// logo rather than shipping a card with a blank image.
const toAbsolute = (src) => {
  if (!src || /\.svg(\?|$)/i.test(src)) return FALLBACK_IMAGE
  if (/^https?:\/\//i.test(src)) return src
  return absoluteUrl(src)
}

/**
 * Per-page SEO tags for the SPA. Renders nothing; syncs document head.
 *
 * Canonical URLs are always built from SITE_URL rather than
 * window.location.origin, so a preview host, a bare IP or a non-www variant
 * never emits a competing canonical for the same content.
 *
 * `jsonLd` accepts a schema.org object or an array of them
 * (Organization, Article, FAQPage, BreadcrumbList, ...).
 * `noindex` marks the page as not indexable (error pages, thin pages).
 */
const Seo = ({ title, description, type = 'website', image, jsonLd, noindex = false }) => {
  const { translations, selectedLanguage } = useContext(LanguageContext)
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : ''
  const { lang: urlLang, base } = splitLocale(typeof window !== 'undefined' ? window.location.pathname : '/')

  // On a language URL (/de/…), use the translated title and description
  // when the locale file has them ("seoT./services", "seoD./services").
  const localTitle = urlLang ? translations[`seoT.${base}`] : null
  const localDescription = urlLang ? translations[`seoD.${base}`] : null
  const pageTitle = localTitle || title
  const pageDescription = localDescription || description

  useEffect(() => {
    const fullTitle = pageTitle ? `${pageTitle} | ${SITE_NAME}` : DEFAULT_TITLE
    const desc = truncate(pageDescription) || DEFAULT_DESCRIPTION
    const url = absoluteUrl(window.location.pathname)
    const img = toAbsolute(image)

    document.title = fullTitle
    upsertMeta('name', 'description', desc)
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow')
    upsertMeta('property', 'og:site_name', SITE_NAME)
    upsertMeta('property', 'og:title', fullTitle)
    upsertMeta('property', 'og:description', desc)
    upsertMeta('property', 'og:type', type)
    upsertMeta('property', 'og:url', url)
    upsertMeta('property', 'og:image', img)
    upsertMeta('property', 'og:image:alt', fullTitle)
    upsertMeta('property', 'og:locale', OG_LOCALE[urlLang || selectedLanguage] || 'en_US')
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', fullTitle)
    upsertMeta('name', 'twitter:description', desc)
    upsertMeta('name', 'twitter:image', img)
    upsertCanonical(url)
    syncAlternates(base)
    upsertJsonLd(jsonLdString ? JSON.parse(jsonLdString) : null)
  }, [pageTitle, pageDescription, type, image, jsonLdString, noindex, urlLang, base, selectedLanguage])

  return null
}

export const organizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/logo.png`,
    width: 600,
    height: 600,
  },
  image: `${SITE_URL}/og-image.png`,
  description: DEFAULT_DESCRIPTION,
  email: CONTACT.email,
  telephone: CONTACT.phone,
  address: {
    '@type': 'PostalAddress',
    addressLocality: CONTACT.city,
    addressCountry: CONTACT.country,
  },
  // Local searches ("AI company Tbilisi") match on where a business works,
  // not only where it is registered.
  areaServed: [
    { '@type': 'City', name: CONTACT.city },
    { '@type': 'Country', name: CONTACT.countryName },
    { '@type': 'Place', name: 'European Union' },
    'Worldwide',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    email: CONTACT.email,
    telephone: CONTACT.phone,
    areaServed: 'Worldwide',
    availableLanguage: ['English', 'Georgian', 'Russian'],
  },
  sameAs: SOCIAL,
})

export const websiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  // Google's site-name algorithm reads these; without them the result shows
  // the bare domain ("mlmind.io") instead of the brand.
  alternateName: ['ML Mind', 'mlmind.io'],
  description: DEFAULT_DESCRIPTION,
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: 'en',
})

export const articleJsonLd = ({ headline, description, datePublished, dateModified, author, image, path }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description: truncate(description),
  datePublished,
  dateModified: dateModified || datePublished,
  image: image ? [toAbsolute(image)] : undefined,
  mainEntityOfPage: path ? { '@type': 'WebPage', '@id': absoluteUrl(path) } : undefined,
  author: { '@type': 'Person', name: author },
  publisher: { '@id': `${SITE_URL}/#organization` },
})

// Rich result for a page that answers questions — the homepage FAQ block.
export const faqJsonLd = (questions) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions
    .filter((q) => q.question && q.answer)
    .map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: { '@type': 'Answer', text: q.answer },
    })),
})

// Product pages: our own software, offered as a service.
export const softwareJsonLd = ({ name, description, image, path, category }) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name,
  description: truncate(description),
  applicationCategory: category || 'BusinessApplication',
  operatingSystem: 'Web',
  image: image ? toAbsolute(image) : undefined,
  url: path ? absoluteUrl(path) : SITE_URL,
  publisher: { '@id': `${SITE_URL}/#organization` },
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    priceSpecification: {
      '@type': 'PriceSpecification',
      priceCurrency: 'USD',
      description: 'Quoted per project after a free discovery call',
    },
  },
})

// Homepage service listing — what an agency actually sells.
export const serviceJsonLd = (services) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'MLMind services',
  itemListElement: services.map((service, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Service',
      name: service.title,
      description: truncate(service.description),
      provider: { '@id': `${SITE_URL}/#organization` },
      areaServed: 'Worldwide',
    },
  })),
})

// A single service page (/services/<slug>).
export const serviceDetailJsonLd = ({ name, description, path, serviceType }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  serviceType: serviceType || name,
  description: truncate(description),
  url: absoluteUrl(path),
  provider: { '@id': `${SITE_URL}/#organization` },
  areaServed: 'Worldwide',
})

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
})

export const articleBreadcrumbs = (title, path) =>
  breadcrumbJsonLd([
    { name: 'Home', path: '/' },
    { name: 'Articles', path: '/articles' },
    { name: title, path },
  ])

export default Seo
